import { useState, useEffect, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Proactive session refresh to prevent expiry
  const refreshSessionIfNeeded = useCallback(async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        const expiresAt = currentSession.expires_at || 0;
        const now = Math.floor(Date.now() / 1000);
        // Refresh if expiring in the next 5 minutes (300 seconds)
        if (expiresAt - now < 300) {
          console.log('Session expiring soon, refreshing...');
          await supabase.auth.refreshSession();
        }
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Periodically check and refresh session (every 60 seconds)
    const refreshInterval = setInterval(refreshSessionIfNeeded, 60000);

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [refreshSessionIfNeeded]);

  const signOut = async () => {
    // Clear workspace and user data from localStorage to prevent accessing previous user's data
    localStorage.removeItem('current_workspace_id');
    localStorage.removeItem('current_user_id');
    await supabase.auth.signOut();
  };

  return { user, session, loading, signOut };
};
